/**
 * Password Reset Confirm page - replaces account/password_reset_from_key.html template
 * This page is shown when user clicks the reset link in their email
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import { renderFieldErrors } from "../components/FieldErrorList";
import {
  useAuthContext,
  useFieldErrors,
  useNonFieldErrors,
} from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";

export function PasswordResetConfirmPage() {
  const { csrfToken, tokenFail, actionUrl } = useAuthContext();
  const nonFieldErrors = useNonFieldErrors();
  const password1Errors = useFieldErrors("password1");
  const password2Errors = useFieldErrors("password2");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");

  const handleSubmit = () => {
    setIsSubmitting(true);
    // Let the form submit naturally to Django
  };

  // Token failure state - link is expired or invalid
  if (tokenFail) {
    return (
      <AuthLayout title={gettext("Bad Token")}>
        <Box
          sx={(theme) => ({
            padding: theme.spacing(0, 5, 5),
            backgroundColor: "white",
          })}
        >
          <Typography
            sx={(theme) => ({
              fontSize: "0.875rem",
              color: BRAND_COLORS.text.secondary,
              marginBottom: theme.spacing(3),
            })}
          >
            {gettext(
              "The password reset link was invalid, possibly because it has already been used.",
            )}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.875rem",
              color: BRAND_COLORS.text.secondary,
            }}
          >
            {gettext("Please request a")}{" "}
            <Link
              href="/accounts/password/reset/"
              sx={{
                color: BRAND_COLORS.link,
                textDecoration: "underline",
                "&:hover": {
                  color: BRAND_COLORS.linkHover,
                },
              }}
            >
              {gettext("new password reset")}
            </Link>
            .
          </Typography>

          <Link
            href="/accounts/login/"
            sx={(theme) => ({
              display: "block",
              textAlign: "center",
              marginTop: theme.spacing(4),
              color: BRAND_COLORS.link,
              fontSize: "0.875rem",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                color: BRAND_COLORS.linkHover,
                textDecoration: "underline",
              },
            })}
            data-testid="back-to-login-link"
          >
            {gettext("Back to Sign In")}
          </Link>
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={gettext("Change Password")}>
      <Box
        component="form"
        method="POST"
        action={actionUrl}
        onSubmit={handleSubmit}
        sx={(theme) => ({
          padding: theme.spacing(0, 5, 5),
          backgroundColor: "white",
        })}
      >
        <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />

        {/* Non-field errors */}
        {nonFieldErrors.length > 0 && (
          <Alert
            severity="error"
            sx={(theme) => ({ marginBottom: theme.spacing(2) })}
            data-testid="password-reset-confirm-error"
          >
            {nonFieldErrors.map((error, index) => (
              <div key={index}>{error}</div>
            ))}
          </Alert>
        )}

        {/* New Password Field */}
        <TextField
          fullWidth
          name="password1"
          type="password"
          label={gettext("New Password")}
          placeholder={gettext("Enter new password")}
          value={password1}
          onChange={(e) => setPassword1(e.target.value)}
          error={password1Errors.length > 0}
          helperText={renderFieldErrors(password1Errors)}
          required
          sx={(theme) => ({
            marginBottom: theme.spacing(3),
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              "&:hover fieldset": {
                borderColor: BRAND_COLORS.inputBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: BRAND_COLORS.inputFocus,
              },
            },
            "& .MuiInputLabel-root": {
              fontWeight: 500,
              color: BRAND_COLORS.text.primary,
            },
          })}
          inputProps={{
            "data-testid": "password1-input",
          }}
        />

        {/* Confirm Password Field */}
        <TextField
          fullWidth
          name="password2"
          type="password"
          label={gettext("Confirm New Password")}
          placeholder={gettext("Confirm new password")}
          value={password2}
          onChange={(e) => setPassword2(e.target.value)}
          error={password2Errors.length > 0}
          helperText={renderFieldErrors(password2Errors)}
          required
          sx={(theme) => ({
            marginBottom: theme.spacing(3),
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontFamily:
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              "&:hover fieldset": {
                borderColor: BRAND_COLORS.inputBorder,
              },
              "&.Mui-focused fieldset": {
                borderColor: BRAND_COLORS.inputFocus,
              },
            },
            "& .MuiInputLabel-root": {
              fontWeight: 500,
              color: BRAND_COLORS.text.primary,
            },
          })}
          inputProps={{
            "data-testid": "password2-input",
          }}
        />

        {/* Submit Button */}
        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={(theme) => ({
            padding: "0.875rem",
            backgroundColor: BRAND_COLORS.primary,
            borderRadius: 2,
            fontSize: "0.9375rem",
            fontWeight: 600,
            textTransform: "none",
            fontFamily: "'Montserrat', sans-serif",
            marginTop: theme.spacing(1),
            "&:hover": {
              backgroundColor: BRAND_COLORS.primaryHover,
              boxShadow: 2,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          })}
          data-testid="change-password-submit-button"
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            gettext("Change Password")
          )}
        </Button>
      </Box>
    </AuthLayout>
  );
}

/**
 * Password Change Done page - shown after successful password change
 * Note: This is different from PasswordResetDonePage which is shown after
 * requesting a password reset email.
 */
export function PasswordChangeDonePage() {
  return (
    <AuthLayout>
      <Box
        sx={(theme) => ({
          padding: theme.spacing(3, 5, 4),
          backgroundColor: "white",
          textAlign: "center",
        })}
      >
        {/* Success checkmark icon */}
        <Box
          sx={(theme) => ({
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "#22C55E",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: `0 auto ${theme.spacing(2)}`,
          })}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
              fill="white"
            />
          </svg>
        </Box>

        <Typography
          variant="h6"
          sx={(theme) => ({
            fontSize: "1.125rem",
            fontWeight: 600,
            color: BRAND_COLORS.text.primary,
            marginBottom: theme.spacing(1),
          })}
        >
          {gettext("Password Changed")}
        </Typography>

        <Typography
          sx={(theme) => ({
            fontSize: "0.875rem",
            color: BRAND_COLORS.text.secondary,
            marginBottom: theme.spacing(3),
          })}
        >
          {gettext("You can now sign in with your new password.")}
        </Typography>

        <Button
          component={Link}
          href="/accounts/login/"
          variant="contained"
          fullWidth
          sx={{
            padding: "0.75rem",
            backgroundColor: BRAND_COLORS.primary,
            borderRadius: 2,
            fontSize: "0.9375rem",
            fontWeight: 600,
            textTransform: "none",
            textDecoration: "none",
            "&:hover": {
              backgroundColor: BRAND_COLORS.primaryHover,
              textDecoration: "none",
            },
          }}
          data-testid="continue-to-login-link"
        >
          {gettext("Sign In")}
        </Button>
      </Box>
    </AuthLayout>
  );
}
